/** Controls all Pulse delivery, including direct flushes and the final startup batch. */
import { GodavariAnalyticsReporterConfiguration } from '../../configuration/GodavariAnalyticsReporterConfiguration';
import { GodavariBatchPayloads, GodavariEmptyBatch } from '../model/GodavariPayload';
import { GodavariConnector } from './GodavariSdk';

type PulseBatch = GodavariBatchPayloads['videoSessionPulse'];
export type GodavariPulseStartEvent = 1 | 2 | 3 | 4 | 5 | 6;

export interface GodavariPulseConfiguration {
  maxPulseBeaconLimit: number;
  startAfterEventPulseBeacon: GodavariPulseStartEvent;
  beaconTimeInterval: number;
}

/** The public config interval uses seconds; the controller timer uses milliseconds. */
export function resolveGodavariPulseConfiguration(
  configuration: GodavariAnalyticsReporterConfiguration,
): GodavariPulseConfiguration {
  const maxPulseBeaconLimit = configuration.maxPulseBeaconLimit ?? 3;
  const startAfterEventPulseBeacon = configuration.startAfterEventPulseBeacon ?? 1;
  const beaconTimeInterval = configuration.beaconTimeInterval ?? 1;
  if (!Number.isInteger(maxPulseBeaconLimit) || maxPulseBeaconLimit < 1 || maxPulseBeaconLimit > 3) {
    throw new RangeError('Godavari maxPulseBeaconLimit must be an integer from 1 to 3, including the final call.');
  }
  if (
    !Number.isInteger(startAfterEventPulseBeacon) ||
    startAfterEventPulseBeacon < 1 ||
    startAfterEventPulseBeacon > 6
  ) {
    throw new RangeError('Godavari startAfterEventPulseBeacon must be an integer from 1 to 6.');
  }
  if (!Number.isFinite(beaconTimeInterval) || beaconTimeInterval <= 0 || beaconTimeInterval * 1000 > 2147483647) {
    throw new RangeError('Godavari beaconTimeInterval must be positive finite seconds within the timer range.');
  }
  return { maxPulseBeaconLimit, startAfterEventPulseBeacon, beaconTimeInterval };
}

export class GodavariPulseController {
  private readonly configuration: GodavariPulseConfiguration;
  private readonly enabled: boolean;
  private timer?: ReturnType<typeof setInterval>;
  private count = 0;
  private started = false;
  private pausedForAd = false;
  private finished = false;
  private disposed = false;
  private pendingBatch?: PulseBatch;

  constructor(
    private readonly connector: GodavariConnector,
    configuration: GodavariAnalyticsReporterConfiguration,
    private readonly createBatch: () => PulseBatch | GodavariEmptyBatch,
    private readonly isCurrentSession: () => boolean,
  ) {
    this.configuration = resolveGodavariPulseConfiguration(configuration);
    this.enabled = configuration.enabled ?? true;
  }

  /** 1 session start, 2 attempt, 3 ad attempt, 4 ad play, 5 ad ended, 6 content play. */
  onEvent(event: GodavariPulseStartEvent): void {
    if (!this.isActive()) return;
    if (event === 6) {
      this.finish();
      return;
    }
    if (!this.finished && !this.started && event === this.configuration.startAfterEventPulseBeacon) {
      this.started = true;
      this.startTimer();
    }
  }

  /** A direct flush consumes the same budget as the periodic callback. */
  flush(): void {
    if (!this.isActive() || !this.started || this.finished || this.pausedForAd) return;
    if (this.count >= this.configuration.maxPulseBeaconLimit - 1) {
      this.stopTimer();
      return;
    }
    this.sendBatch();
    if (this.count >= this.configuration.maxPulseBeaconLimit - 1) this.stopTimer();
  }

  pauseForAd(): void {
    this.pausedForAd = true;
    this.stopTimer();
  }

  resumeAfterAd(): void {
    this.pausedForAd = false;
    this.startTimer();
  }

  /** Final delivery also covers early failure before the configured start event. */
  finish(): void {
    this.stopTimer();
    if (!this.isActive() || this.finished) return;
    if (this.count < this.configuration.maxPulseBeaconLimit) this.sendBatch();
    this.finished = true;
  }

  /** Only call when a retry restarts the startup flow from its first step. */
  resetForRetry(): void {
    if (!this.isActive()) return;
    this.stopTimer();
    this.count = 0;
    this.started = false;
    this.finished = false;
  }

  dispose(): void {
    this.stopTimer();
    this.disposed = true;
    this.pendingBatch = undefined;
  }

  private isActive(): boolean {
    return this.enabled && !this.disposed && this.isCurrentSession();
  }

  private startTimer(): void {
    this.stopTimer();
    if (!this.isActive() || !this.started || this.finished || this.pausedForAd) return;
    if (this.count >= this.configuration.maxPulseBeaconLimit - 1) return;
    this.timer = setInterval(() => {
      if (!this.isActive()) {
        this.stopTimer();
        return;
      }
      this.flush();
    }, this.configuration.beaconTimeInterval * 1000);
  }

  private stopTimer(): void {
    if (this.timer !== undefined) clearInterval(this.timer);
    this.timer = undefined;
  }

  private sendBatch(): void {
    const next = this.createBatch();
    if (next.event_data_list.length) {
      this.pendingBatch = {
        ...(next as PulseBatch),
        event_data_list: [...(this.pendingBatch?.event_data_list || []), ...next.event_data_list],
      };
    }
    if (!this.pendingBatch) return;
    const payload = { ...this.pendingBatch, pulse_event_count_sequence: this.count + 1 };
    // Retain drained observations if the vendor dispatch throws synchronously.
    this.connector.videoSessionPulse(payload.video_event_type, payload);
    this.count++;
    this.pendingBatch = undefined;
  }
}
